import { ReactiveController, ReactiveControllerHost } from "lit";
import N3xusApi from '../base/n3xus/N3xusAPI.js';
import { 
    N3xusApiConfig, 
    AccessCheckData, 
    MintData, 
    StatusData, 
    ActivationData,
    CampaignStatusData,
    CampaignInformationData,
    MintStatus,
    Environment
} from '../base/n3xus/types.js';

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    expiresAt: number;
}

interface WalletCheckCache {
    [key: string]: CacheEntry<AccessCheckData>;
}

interface CampaignInfoCache {
    [key: string]: CacheEntry<CampaignInformationData>;
}

interface CampaignStatusCache {
    [key: string]: CacheEntry<CampaignStatusData>;
}

interface MintRequest {
    didToken: string;
    issuanceCode?: string;
    campaignUrl?: string;
    campaignId?: number;
    status: 'pending' | 'completed' | 'failed' | 'redeemed';
    error?: string;
}

export const ALREADY_MINTED_ERROR = 'You already have this campaign';

/**
 * The `N3xusAPIController` class is responsible for managing multiple N3xus API instances.
 * It implements the `ReactiveController` interface to interact with reactive hosts.
 * This class follows the singleton pattern to ensure only one instance is created.
 * 
 * @implements {ReactiveController}
 */
export class N3xusAPIController implements ReactiveController {
    private static instance: N3xusAPIController;
    private hosts: Set<ReactiveControllerHost> = new Set();
    private apiInstances: Map<string, N3xusApi> = new Map();
    private debug: boolean = false;
    
    // Cache storage with expiration
    private walletCheckCache: WalletCheckCache = {};
    private campaignInfoCache: CampaignInfoCache = {};
    private campaignStatusCache: CampaignStatusCache = {};
    private issueStatusCache: { [key: string]: CacheEntry<StatusData> } = {};
    private readonly CACHE_DURATION = 1 * 60 * 1000; // 1 minutes in milliseconds
    private readonly WALLET_CHECK_CACHE_DURATION = 1 * 60 * 1000; // 1 minutes in milliseconds
    private readonly ISSUE_STATUS_CACHE_DURATION = 1 * 60 * 1000; // 1 minutes in milliseconds

    // Mint request tracking
    private activeMintRequests: Map<string, MintRequest> = new Map();
    private readonly POLL_INTERVAL = 2000; // 2 seconds
    private pollTimers: Map<number, ReturnType<typeof setInterval>> = new Map();
    private issueIdToRequestKeys: Map<number, Set<string>> = new Map();

    // In-progress request tracking
    private inProgressRequests: Map<string, Promise<any>> = new Map();

    /**
     * Creates an instance of N3xusAPIController.
     * If an instance already exists, it returns the existing instance.
     * 
     * @param {boolean} [debug=false] - Optional debug flag to enable or disable debug mode.
     * @returns {N3xusAPIController} The singleton instance of N3xusAPIController.
     */    
    constructor(debug: boolean = false) {
        if (N3xusAPIController.instance) {
            return N3xusAPIController.instance;
        }
        this.debug = debug;
        N3xusAPIController.instance = this;
    }

    /**
     * Gets or creates a N3xus API instance for the given API key.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @returns {N3xusApi} The N3xus API instance.
     */
    public getAPI(config: N3xusApiConfig): N3xusApi {
        const key = `${config.apiKey}-${config.environment || Environment.PRODUCTION}`;
        
        if (!this.apiInstances.has(key)) {
            this.logging('Creating new N3xus API instance for key:', key);
            this.apiInstances.set(key, new N3xusApi(config));
        }
        
        return this.apiInstances.get(key)!;
    }

    /**
     * Checks the status of an issue.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {number | null} issueId - The ID of the issue to check.
     * @param {string | null} redeemCode - The redeem code to check.
     * @param {boolean} bypassCache - Whether to bypass the cache.
     * @returns {Promise<StatusData>} A promise that resolves to the status data.
     */
    public async checkIssueStatus(config: N3xusApiConfig, issueId: number | null, redeemCode: string | null, bypassCache: boolean = false): Promise<StatusData> {
        const envKey = config.environment === Environment.LOCAL ? 'l' : config.environment === Environment.DEVELOPMENT ? 'd' : config.environment === Environment.STAGING ? 's' : 'p';
        const cacheKey = `${config.apiKey}_${envKey}_${issueId || redeemCode}`;
        
        // Check for in-progress request first
        const inProgressRequest = this.inProgressRequests.get(cacheKey);
        if (inProgressRequest) {
            this.logging('Using in-progress request for:', cacheKey);
            return inProgressRequest;
        }

        // Check for cached data only if not bypassing cache
        if (!bypassCache) {
            const cachedData = this.getCachedData<StatusData>(this.issueStatusCache, cacheKey);
            if (cachedData) {
                this.logging('Using cached issue status for:', cacheKey);
                return cachedData;
            }
        }

        // Create new request
        const request = (async () => {
            try {
                const api = this.getAPI(config);
                const response = await api.checkIssueStatus(issueId, redeemCode);
                
                if (!response.ok) {
                    throw new Error(response.error || 'Failed to check issue status');
                }

                // Only cache if not bypassing cache
                if (!bypassCache) {
                    this.cacheData<StatusData>(this.issueStatusCache, cacheKey, response, this.ISSUE_STATUS_CACHE_DURATION);
                }
                return response;
            } finally {
                this.inProgressRequests.delete(cacheKey);
            }
        })();

        // Store the request
        this.inProgressRequests.set(cacheKey, request);
        return request;
    }

    /**
     * Checks if a wallet holds an NFT with caching.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {string} unlockMetadata - The unlock details.
     * @param {string} didToken - The DID token.
     * @returns {Promise<AccessCheckData>} A promise that resolves to the access check data.
     */
    public async checkWalletForNft(config: N3xusApiConfig, unlockMetadata: string, didToken: string, bypassCache: boolean = false): Promise<AccessCheckData> {
        // Take first 32 characters of the didToken for uniqueness
        const didTokenPrefix = didToken.substring(0, 5);
        const envKey = config.environment === Environment.LOCAL ? 'l' : config.environment === Environment.DEVELOPMENT ? 'd' : config.environment === Environment.STAGING ? 's' : 'p';
        const cacheKey = `${config.apiKey}_${envKey}_${didTokenPrefix}`;
                
        // Check for in-progress request first
        const inProgressRequest = this.inProgressRequests.get(cacheKey);
        if (inProgressRequest) {
            this.logging('Using in-progress wallet check for:', cacheKey);
            return inProgressRequest;
        }

        // Check for cached data
        const cachedData = this.getCachedData<AccessCheckData>(this.walletCheckCache, cacheKey);
        if (cachedData && !bypassCache) {
            this.logging('Using cached wallet check for:', cacheKey);
            return cachedData;
        }

        // Create new request and store it immediately
        const request = (async () => {
            try {
                const api = this.getAPI(config);
                const result = await api.checkWalletForNft(unlockMetadata, didToken);
                this.cacheData<AccessCheckData>(this.walletCheckCache, cacheKey, result, this.WALLET_CHECK_CACHE_DURATION);
                return result;
            } finally {
                this.inProgressRequests.delete(cacheKey);
            }
        })();

        // Store the request BEFORE returning it
        this.inProgressRequests.set(cacheKey, request);
        return request;
    }

    /**
     * Checks if there's an active mint request for the given parameters.
     * 
     * @param {string} didToken - The DID token.
     * @param {string} [issuanceCode] - The issuance code.
     * @param {number} [campaignId] - The campaign ID.
     * @returns {boolean} True if there's an active mint request.
     */
    private hasActiveMintRequest(didToken: string, issuanceCode?: string, campaignId?: number): boolean {
        const key = this.getMintRequestKey(didToken, issuanceCode, campaignId);
        const request = this.activeMintRequests.get(key);
        return request?.status === 'pending';
    }

    /**
     * Gets a unique key for a mint request.
     * 
     * @param {string} didToken - The DID token.
     * @param {string} [issuanceCode] - The issuance code.
     * @param {number} [campaignId] - The campaign ID.
     * @returns {string} The unique key for the mint request.
     */
    private getMintRequestKey(didToken: string, issuanceCode?: string, campaignId?: number): string {
        return `${didToken}_${issuanceCode || ''}_${campaignId || ''}`;
    }

    /**
     * Starts polling for the status of a mint request.
     * 
     * @param {string} key - The mint request key.
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {MintRequest} request - The mint request to poll for.
     * @param {number} issueId - The issue ID to poll for.
     */
    private startPolling(key: string, config: N3xusApiConfig, issueId: number): void {
        // If we're already polling this issueId, just add this request to the set
        if (this.pollTimers.has(issueId)) {
            const requestKeys = this.issueIdToRequestKeys.get(issueId) || new Set();
            requestKeys.add(key);
            this.issueIdToRequestKeys.set(issueId, requestKeys);
            return;
        }

        const poll = async () => {
            try {
                const status = await this.checkIssueStatus(
                    config,
                    issueId,
                    null,
                    true // Bypass cache when polling
                );

                if (!status.ok) {
                    this.updateMintRequestsForIssueId(issueId, 'failed', status.error || 'Mint failed');
                    this.stopPolling(issueId);
                    return;
                }

                const mintStatus = status.data?.status;
                if (mintStatus === MintStatus.redeemed) {
                    this.updateMintRequestsForIssueId(issueId, 'completed');
                    this.stopPolling(issueId);
                } else if (mintStatus === MintStatus.error) {
                    this.updateMintRequestsForIssueId(issueId, 'failed', status.error || 'Mint failed');
                    this.stopPolling(issueId);
                }
            } catch (error) {
                this.updateMintRequestsForIssueId(issueId, 'failed', error instanceof Error ? error.message : 'Unknown error');
                this.stopPolling(issueId);
            }
        };

        const timer = setInterval(poll, this.POLL_INTERVAL);
        this.pollTimers.set(issueId, timer);
        
        // Initialize the set of request keys for this issueId
        const requestKeys = new Set<string>([key]);
        this.issueIdToRequestKeys.set(issueId, requestKeys);
        
        poll(); // Initial poll
    }

    /**
     * Updates all mint requests associated with an issueId.
     * 
     * @param {number} issueId - The issue ID.
     * @param {'completed' | 'failed'} status - The new status.
     * @param {string} [error] - Optional error message.
     */
    private updateMintRequestsForIssueId(issueId: number, status: 'completed' | 'failed', error?: string): void {
        const requestKeys = this.issueIdToRequestKeys.get(issueId);
        if (!requestKeys) return;

        for (const key of requestKeys) {
            const request = this.activeMintRequests.get(key);
            if (request) {
                this.activeMintRequests.set(key, {
                    ...request,
                    status,
                    ...(error && { error })
                });
            }
        }
        this.notifyHosts();
    }

    /**
     * Stops polling for a mint request.
     * 
     * @param {number} issueId - The issue ID.
     */
    private stopPolling(issueId: number): void {
        const timer = this.pollTimers.get(issueId);
        if (timer) {
            clearInterval(timer);
            this.pollTimers.delete(issueId);
            this.issueIdToRequestKeys.delete(issueId);
        }
    }

    /**
     * Mints a new NFT with request tracking.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {string} didToken - The DID token.
     * @param {string} [issuanceCode] - The issuance code.
     * @param {string} [campaignUrl] - The campaign URL.
     * @param {number} [campaignId] - The campaign ID.
     * @returns {Promise<MintData>} A promise that resolves to the mint data.
     */
    public async mint(
        config: N3xusApiConfig,
        didToken: string,
        issuanceCode?: string,
        campaignUrl?: string,
        campaignId?: number
    ): Promise<MintData> {
        const key = this.getMintRequestKey(didToken, issuanceCode, campaignId);
        const cacheKey = `${config.apiKey}_${config.environment || Environment.PRODUCTION}_${key}`;

        // Check for in-progress mint request
        const inProgressRequest = this.inProgressRequests.get(cacheKey);
        if (inProgressRequest) {
            this.logging('Using in-progress mint request for:', cacheKey);
            return inProgressRequest;
        }

        if (this.hasActiveMintRequest(didToken, issuanceCode, campaignId)) {
            throw new Error('A mint request is already in progress');
        }

        const request: MintRequest = {
            didToken,
            issuanceCode,
            campaignUrl,
            campaignId,
            status: 'pending'
        };

        this.activeMintRequests.set(key, request);
        this.notifyHosts();

        // Create new mint request
        const mintPromise = (async () => {
            try {
                const api = this.getAPI(config);
                const result = await api.mint(didToken, issuanceCode, campaignUrl, campaignId);
                console.log('result', result)
                
                if( result.already_minted && result.already_minted === true) {
                    throw new Error(ALREADY_MINTED_ERROR);
                }

                // Start polling for status using the issueId
                this.startPolling(key, config, result.issueId);
                return result;
            } catch (error) {
                console.error('ERROR', error)
                if (error instanceof Error && error.message.toLowerCase().includes(ALREADY_MINTED_ERROR.toLowerCase())) {
                    this.activeMintRequests.set(key, { 
                        ...request, 
                        status: 'redeemed',
                        error: ALREADY_MINTED_ERROR
                    });
                } else {
                    this.activeMintRequests.set(key, { 
                        ...request, 
                        status: 'failed',
                        error: error instanceof Error ? error.message : 'Unknown error'
                    });
                }

                this.notifyHosts();
                throw error;
            } finally {
                this.inProgressRequests.delete(cacheKey);
            }
        })();

        // Store the request
        this.inProgressRequests.set(cacheKey, mintPromise);
        return mintPromise;
    }

    /**
     * Gets the status of a mint request.
     * 
     * @param {string} didToken - The DID token.
     * @param {string} [issuanceCode] - The issuance code.
     * @param {number} [campaignId] - The campaign ID.
     * @returns {MintRequest | undefined} The mint request status.
     */
    public getMintRequestStatus(
        didToken: string,
        issuanceCode?: string,
        campaignId?: number
    ): MintRequest | undefined {
        const key = this.getMintRequestKey(didToken, issuanceCode, campaignId);
        return this.activeMintRequests.get(key);
    }

    /**
     * Logs an activation.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {string} contract_address - The contract address.
     * @param {string} type - The type of activation.
     * @param {string} wallet_address - The wallet address.
     * @returns {Promise<ActivationData>} A promise that resolves to the activation data.
     */
    public async logActivation(
        config: N3xusApiConfig,
        contract_address: string,
        type: string,
        wallet_address: string
    ): Promise<ActivationData> {
        const api = this.getAPI(config);
        return api.logActivation(contract_address, type, wallet_address);
    }

    /**
     * Checks the status of a campaign.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {string} campaignUrl - The campaign URL.
     * @returns {Promise<CampaignStatusData>} A promise that resolves to the campaign status data.
     */
    public async checkCampaign(config: N3xusApiConfig, campaignUrl: string): Promise<CampaignStatusData> {
        const cacheKey = `${config.apiKey}_${config.environment || Environment.PRODUCTION}_${campaignUrl}`;
        
        // Check for in-progress request first
        const inProgressRequest = this.inProgressRequests.get(cacheKey);
        if (inProgressRequest) {
            this.logging('Using in-progress request for:', cacheKey);
            return inProgressRequest;
        }

        // Check for cached data
        const cachedData = this.getCachedData<CampaignStatusData>(this.campaignStatusCache, cacheKey);
        if (cachedData) {
            this.logging('Using cached campaign status for:', cacheKey);
            return cachedData;
        }

        // Create new request
        const request = (async () => {
            try {
                const api = this.getAPI(config);
                const result = await api.checkCampaign(campaignUrl);
                this.cacheData<CampaignStatusData>(this.campaignStatusCache, cacheKey, result);
                return result;
            } finally {
                this.inProgressRequests.delete(cacheKey);
            }
        })();

        // Store the request
        this.inProgressRequests.set(cacheKey, request);
        return request;
    }

    /**
     * Gets campaign information with caching and in-progress request tracking.
     * 
     * @param {N3xusApiConfig} config - The configuration for the N3xus API instance.
     * @param {number} campaignId - The campaign ID.
     * @returns {Promise<CampaignInformationData>} A promise that resolves to the campaign information data.
     */
    public async getCampaignInformation(config: N3xusApiConfig, campaignId: number): Promise<CampaignInformationData> {
        const cacheKey = `${config.apiKey}_${config.environment || Environment.PRODUCTION}_${campaignId}`;
        
        // Check for cached data
        const cachedData = this.getCachedData<CampaignInformationData>(this.campaignInfoCache, cacheKey);
        if (cachedData) {
            this.logging('Using cached campaign information for:', cacheKey);
            return cachedData;
        }

        // Check for in-progress request
        const inProgressRequest = this.inProgressRequests.get(cacheKey);
        if (inProgressRequest) {
            this.logging('Using in-progress request for:', cacheKey);
            return inProgressRequest;
        }

        // Create new request
        const request = (async () => {
            try {
                const api = this.getAPI(config);
                const result = await api.getCampaignInformation(campaignId);
                this.cacheData<CampaignInformationData>(this.campaignInfoCache, cacheKey, result);
                return result;
            } finally {
                this.inProgressRequests.delete(cacheKey);
            }
        })();

        // Store the request
        this.inProgressRequests.set(cacheKey, request);
        return request;
    }

    /**
     * Gets cached data if it exists and is not expired.
     * 
     * @param {Object} cache - The cache object to check.
     * @param {string} key - The cache key.
     * @returns {T | null} The cached data or null if not found or expired.
     */
    private getCachedData<T>(cache: { [key: string]: CacheEntry<T> }, key: string): T | null {
        const entry = cache[key];
        if (!entry) return null;

        const now = Date.now();
        if (now > entry.expiresAt) {
            delete cache[key];
            return null;
        }

        return entry.data;
    }

    /**
     * Caches data with an expiration timestamp.
     * 
     * @param {Object} cache - The cache object to store in.
     * @param {string} key - The cache key.
     * @param {T} data - The data to cache.
     * @param {number} [duration] - Optional custom cache duration in milliseconds.
     */
    private cacheData<T>(cache: { [key: string]: CacheEntry<T> }, key: string, data: T, duration?: number): void {
        const now = Date.now();
        cache[key] = {
            data,
            timestamp: now,
            expiresAt: now + (duration || this.CACHE_DURATION)
        };
    }

    /**
     * Clears all cached data and active mint requests.
     */
    public clearCache(): void {
        this.walletCheckCache = {};
        this.campaignInfoCache = {};
        this.campaignStatusCache = {};
        this.issueStatusCache = {};
        this.activeMintRequests.clear();
        this.inProgressRequests.clear();
        for (const [issueId] of this.pollTimers) {
            this.stopPolling(issueId);
        }
        this.issueIdToRequestKeys.clear();
    }

    /**
     * Adds a host to the set of hosts and registers this controller with the host.
     * 
     * @param host - The ReactiveControllerHost instance to be added.
     */
    public addHost(host: ReactiveControllerHost): void {
        if (!this.hosts.has(host)) {
            this.hosts.add(host);
            host.addController(this);
        }
    }

    /**
     * Removes the specified host from the collection of hosts and 
     * removes this controller from the host.
     *
     * @param host - The host to be removed.
     */
    public removeHost(host: ReactiveControllerHost): void {
        if (this.hosts.has(host)) {
            this.hosts.delete(host);
            host.removeController(this);
        }
    }

    /**
     * Notifies all hosts to request an update.
     * 
     * @private
     */
    private notifyHosts(): void {
        for (const host of this.hosts) {
            host.requestUpdate();
        }
    }

    /**
     * Get the singleton instance of the N3xusAPIController.
     *
     * @param {boolean} [debug=false] - Whether to enable debug logging.
     * @returns {N3xusAPIController} The singleton instance of the N3xusAPIController.
     */
    static getInstance(debug?: boolean): N3xusAPIController {
        if (!N3xusAPIController.instance) {
            N3xusAPIController.instance = new N3xusAPIController(debug);
        }
        return N3xusAPIController.instance;
    }

    hostConnected(): void {
        // Implementation for hostConnected
    }

    hostDisconnected(): void {
        // Implementation for hostDisconnected
    }

    /**
     * Logs a message to the console if debugging is enabled.
     *
     * @param message - The primary message to log.
     * @param optionalParams - Additional optional parameters to log.
     */
    logging(message?: any, ...optionalParams: any[]): void {
        if (this.debug) {
            console.log(`[N3xusAPIController] ${message}`, ...optionalParams);
        }
    }
} 